/**
 * Authentication Token Management
 * Handles storage and retrieval of API tokens using cookies
 *
 * Backend JWT: expiresIn 86400 (24h) - isTokenExpired() checks JWT exp claim
 * Cookie storage: 30 days - how long browser keeps the cookie
 */

const TOKEN_COOKIE_NAME = 'eclipsera_api_token'
const COOKIE_EXPIRY_DAYS = 30 // 1 month

/**
 * Set a cookie
 */
const setCookie = (name: string, value: string, days: number): void => {
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`
}

/**
 * Get a cookie value
 */
const getCookie = (name: string): string | null => {
  const nameEQ = name + '='
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
  }
  return null
}

/**
 * Delete a cookie
 */
const deleteCookie = (name: string): void => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`
}

/**
 * Store the API token in a cookie (expires in 30 days)
 */
export const setAuthToken = (token: string | null): void => {
  if (token) {
    setCookie(TOKEN_COOKIE_NAME, token, COOKIE_EXPIRY_DAYS)
  } else {
    deleteCookie(TOKEN_COOKIE_NAME)
  }
}

/**
 * Get the API token from cookie
 */
export const getAuthToken = (): string | null => {
  return getCookie(TOKEN_COOKIE_NAME)
}

/**
 * Clear the API token from cookie
 */
export const clearAuthToken = (): void => {
  deleteCookie(TOKEN_COOKIE_NAME)
}

/**
 * Check if a token exists
 */
export const hasAuthToken = (): boolean => {
  return !!getAuthToken()
}

/**
 * Decode and validate token data (without verification)
 * Returns token payload or null if invalid
 */
export const decodeTokenData = (): {
  id?: string
  walletAddress?: string
  role?: number
  exp?: number
  iat?: number
} | null => {
  const token = getAuthToken()
  if (!token) {
    return null
  }

  try {
    // JWT tokens have 3 parts separated by dots
    const parts = token.split('.')
    if (parts.length !== 3) {
      return null
    }

    // Decode the payload (second part) - base64url decode
    const payload = parts[1]
    const decoded = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    )
    
    return decoded
  } catch (error) {
    console.error('Error decoding token:', error)
    return null
  }
}

/**
 * Check if token is expired (based on exp claim)
 */
export const isTokenExpired = (): boolean => {
  const tokenData = decodeTokenData()
  if (!tokenData || !tokenData.exp) {
    return true
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = tokenData.exp * 1000
  return Date.now() >= expirationTime
}

/**
 * Get token info for debugging/validation
 */
export const getTokenInfo = (): {
  exists: boolean
  expired: boolean
  data: ReturnType<typeof decodeTokenData>
} => {
  const exists = hasAuthToken()
  const data = decodeTokenData()
  const expired = isTokenExpired()

  return {
    exists,
    expired,
    data,
  }
}

